/**
 * @file ClientServicesCloud.ts
 * @description Modulo centralizzato per la gestione di tutti i client API esterni forniti dal team .NET.
 *
 * ARCHITETTURA:
 * Questo file funge da punto unico di configurazione e creazione per tutti i client API.
 * I package @bludata/fc-* contengono client generati dal team .NET che comunicano con i microservizi backend.
 *
 * PERCHÉ CENTRALIZZARE:
 * 1. Singolo punto di gestione token - updateTokenJWTFactory() configura tutti i client
 * 2. Manutenibilità - aggiungere un nuovo client richiede modifica solo qui
 * 3. Type-safety - clientsDefinitions con generics garantisce tipi corretti
 * 4. Lazy creation - i client vengono creati solo quando richiesti via getter
 * 5. Testabilità - mock semplificato per unit test
 *
 * FLUSSO:
 * 1. Al login, MSAL restituisce un token JWT
 * 2. updateTokenJWTFactory(token) inizializza ApiFactory con baseURI e token
 * 3. I getter (es. getDispatcherClient()) creano client on-demand
 *
 * NOTA: I getter ritornano undefined se chiamati prima di updateTokenJWTFactory()
 *
 * @see .github/skills/external-clients/SKILL.md per documentazione completa
 */

import { LegacyConnectorClient } from '@bludata/fc-legacyconnector';
import { ApiFactory } from '@bludata/common-ui';
import {
  DispatcherClient,
  MessageTemplatesClient,
  MessageAccountsClient,
  MessageWebhooksClient,
} from '@bludata/fc-dispatcher';
import {
  MessageDTO,
  MessagesClient,
  MessageRecipientClient,
  MessageRecipientWithCountsDTO,
  BluSignClient,
  BluSignCustomerSignClient,
  QueryBuilderClient,
  AnalysisClient,
  AuditsClient,
  ActionMessagingClient,
  WebhooksClient,
} from '@bludata/fc-core';
import { StorageClient } from '@bludata/fc-storage';
import { BiClient } from '@bludata/fc-bi';
import {
  ApiKeysClient,
  OperatorsClient,
  PoliciesClient,
  WorkplacesClient,
  LicensesClient,
  CompaniesClient,
} from '@bludata/fc-registry';
import { NewsClient, ReleasesClient } from '@bludata/fc-aggregator';
import { AadB2cUserClient } from '@bludata/fc-adb2cconnector';
import {
  getJwtTokenType,
  resolveImpersonationMode,
} from '../msal/resolveImpersonationMode';

/**
 * Stato interno del modulo.
 * Contiene il token JWT corrente e l'istanza di ApiFactory.
 *
 * @property token - Token JWT ottenuto da MSAL dopo il login
 * @property apiFactory - Factory per la creazione dei client, undefined se non inizializzata
 */
interface ClientServicesMap {
  token: string;
  apiFactory: ApiFactory | undefined;
}

export const getBaseURI = () => {
  const { isImpersonation } = resolveImpersonationMode({
    currentOrigin: window.location.origin,
    impersonationRedirectUri: import.meta.env
      .VITE_NX_PUBLIC_REDIRECT_URI_IMPERSONATE,
    tokenType: getJwtTokenType(clientServicesMap.token),
  });

  if (isImpersonation)
    return import.meta.env.VITE_NX_PUBLIC_INIT_URI_IMPERSONATE;
  return import.meta.env.VITE_NX_PUBLIC_INIT_URI;
};

/**
 * Definizione di un client nel registro.
 * Ogni client ha un costruttore, il tipo di factory da usare e una funzione creator.
 *
 * @template T - Tipo del client (es. DispatcherClient, StorageClient)
 * @property constructor - Classe del client da istanziare
 * @property factoryType - Chiave della factory da usare (sempre 'apiFactory')
 * @property creator - Funzione che usa la factory per creare l'istanza del client
 */
interface ClientDefinition<T> {
  constructor: any;
  factoryType: keyof Pick<ClientServicesMap, 'apiFactory'>;
  creator: (factory: any, constructor?: any) => T;
}

/**
 * Stato singleton del modulo.
 * Inizializzato vuoto, viene popolato da updateTokenJWTFactory() dopo il login.
 */
const clientServicesMap: ClientServicesMap = {
  token: '',
  apiFactory: undefined,
};

/**
 * Registro di tutti i client disponibili.
 *
 * Per aggiungere un nuovo client:
 * 1. Importare la classe dal package @bludata/fc-*
 * 2. Aggiungere entry qui con constructor, factoryType e creator
 * 3. Creare getter function dedicata (es. getNuovoClient)
 * 4. Esportare il getter
 *
 * NOTA: Usare createGeneric per client read-only, createReadWriteTyped per client CRUD
 */
const clientsDefinitions = {
  legacyConnectorClient: {
    constructor: LegacyConnectorClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<LegacyConnectorClient>,

  newsClient: {
    constructor: NewsClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<NewsClient>,

  releasesClient: {
    constructor: ReleasesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<ReleasesClient>,

  dispatcherClient: {
    constructor: DispatcherClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<DispatcherClient>,

  messageClient: {
    constructor: MessagesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createReadWriteTyped<MessagesClient, MessageDTO>(MessagesClient),
  } as ClientDefinition<MessagesClient>,

  messageRecipientClient: {
    constructor: MessageRecipientClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createReadWriteTyped<
        MessageRecipientClient,
        MessageRecipientWithCountsDTO
      >(MessageRecipientClient),
  } as ClientDefinition<MessageRecipientClient>,

  templateClient: {
    constructor: MessageTemplatesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<MessageTemplatesClient>,

  messageAccountsClient: {
    constructor: MessageAccountsClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<MessageAccountsClient>,

  messageWebhooksClient: {
    constructor: MessageWebhooksClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<MessageWebhooksClient>,

  queryBuilderClient: {
    constructor: QueryBuilderClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<QueryBuilderClient>,

  storageClient: {
    constructor: StorageClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<StorageClient>,

  bluSignClient: {
    constructor: BluSignClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<BluSignClient>,

  bluSignCustomerClient: {
    constructor: BluSignCustomerSignClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<BluSignCustomerSignClient>,

  biClient: {
    constructor: BiClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) =>
      factory.createGeneric(constructor),
  } as ClientDefinition<BiClient>,

  policiesClient: {
    constructor: PoliciesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<PoliciesClient>,

  apiKeysClient: {
    constructor: ApiKeysClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<ApiKeysClient>,

  aadB2cUserClient: {
    constructor: AadB2cUserClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<AadB2cUserClient>,

  operatorsClient: {
    constructor: OperatorsClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<OperatorsClient>,

  workplacesClient: {
    constructor: WorkplacesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<WorkplacesClient>,

  licensesClient: {
    constructor: LicensesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<LicensesClient>,

  analysisClient: {
    constructor: AnalysisClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<AnalysisClient>,

  auditsClient: {
    constructor: AuditsClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<AuditsClient>,

  companiesClient: {
    constructor: CompaniesClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<CompaniesClient>,

  actionMessagingClient: {
    constructor: ActionMessagingClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<ActionMessagingClient>,

  webhooksClient: {
    constructor: WebhooksClient,
    factoryType: 'apiFactory',
    creator: (factory: ApiFactory, constructor: any) => {
      return factory.createGeneric(constructor);
    },
  } as ClientDefinition<WebhooksClient>,
};

// Tipo che rappresenta le chiavi dei client disponibili
type ClientKey = keyof typeof clientsDefinitions;

/**
 * Restituisce un client specifico in base alla chiave fornita.
 * Rigenera sempre una nuova istanza del client.
 *
 * @template K - La chiave del client nel registro dei client.
 * @param clientKey - La chiave del client da creare.
 * @returns Una nuova istanza del client, oppure `undefined` se non è possibile creare il client.
 */
function getClientByKey<K extends ClientKey>(
  clientKey: K,
): ReturnType<(typeof clientsDefinitions)[K]['creator']> | undefined {
  const clientDef = clientsDefinitions[clientKey];
  const factory = clientServicesMap[clientDef.factoryType];

  if (!factory) return undefined;

  return clientDef.creator(factory, clientDef.constructor) as any;
}

/**
 * Configura una nuova istanza di ApiFactory con un token specifico.
 */
export const initializeApiFactory = (token: string) => {
  if (token) {
    clientServicesMap.apiFactory = new ApiFactory(getBaseURI(), token);
  }
};

/**
 * Imposta il token JWT e aggiorna l'istanza di ApiFactory.
 */
export const updateTokenJWTFactory = (token: string) => {
  if (!token || typeof token !== 'string') return;

  clientServicesMap.token = token;
  initializeApiFactory(token);
  // initializeAggregatorApiFactory(token);
};

/**
 * Restituisce un'istanza di DispatcherClient.
 */
export const getDispatcherClient = () => getClientByKey('dispatcherClient');
/**
 * Restituisce un'istanza di messageClient.
 */
export const getMessageClient = () => getClientByKey('messageClient');
/**
 * Restituisce un'istanza di messageRecipientClient.
 */
export const getMessageRecipientClient = () =>
  getClientByKey('messageRecipientClient');
/**
 * Restituisce un'istanza di templateClient.
 */
export const getTemplateClient = () => getClientByKey('templateClient');

/**
 * Restituisce un'istanza di messageAccountsClient.
 */
export const getMessageAccountsClient = () =>
  getClientByKey('messageAccountsClient');

/**
 * Restituisce un'istanza di messageWebhooksClient.
 */
export const getMessageWebhooksClient = () =>
  getClientByKey('messageWebhooksClient');

/**
 * Restituisce un'istanza di LegacyConnectorClient.
 */
export const getLegacyConnectorClient = () =>
  getClientByKey('legacyConnectorClient');

/**
 * Restituisce un'istanza di StorageClient.
 */
export const getStorageClient = () => getClientByKey('storageClient');

/**
 * Restituisce un'istanza di BluSignClient.
 */
export const getBluSignClient = () => getClientByKey('bluSignClient');

/**
 * Restituisce un'istanza di bluSignCustomerClient.
 */
export const getBluSignCustomerClient = () =>
  getClientByKey('bluSignCustomerClient');

/**
 * Restituisce un'istanza di BiClient.
 */
export const getBiClient = () => getClientByKey('biClient');

/**
 * Restituisce un'istanza di NewsClient.
 */
export const getNewsClient = () => getClientByKey('newsClient');

/**
 * Restituisce un'istanza di ReleasesClient.
 */
export const getReleasesClient = () => getClientByKey('releasesClient');

/**
 * Restituisce un'istanza di PoliciesClient.
 */
export const getPoliciesClient = () => getClientByKey('policiesClient');

export const getApiKeysClient = () => getClientByKey('apiKeysClient');

export const getAadB2cUserClient = () => getClientByKey('aadB2cUserClient');

export const getOperatorsClient = () => getClientByKey('operatorsClient');

export const getWorkplacesClient = () => getClientByKey('workplacesClient');

export const getLicensesClient = () => getClientByKey('licensesClient');
/**
 * Restituisce un'istanza di AnalysisClient.
 */
export const getAnalysisClient = () => getClientByKey('analysisClient');

/**
 * Restituisce un'istanza di QueryBuilderClient.
 */
export const getQueryBuilderClient = () => getClientByKey('queryBuilderClient');

export const getAuditsClient = () => getClientByKey('auditsClient');

export const getCompaniesClient = () => getClientByKey('companiesClient');

/**
 * Restituisce un'istanza di DocumentMessagingClient.
 */
export const getActionMessagingClient = () =>
  getClientByKey('actionMessagingClient');

/**
 * Restituisce un'istanza di WebhooksClient.
 */
export const getWebhooksClient = () => getClientByKey('webhooksClient');

/**
 * Restituisce il token JWT salvato.
 */
export const getTokenJWT = (): string => clientServicesMap.token;
